import type { ComputedRef, Ref } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Hit } from '@nuxtjs/algolia'
import {
  EventListPageType,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { LocalityCategory } from '~/composables/types/locality-category'
import type { Category } from '~/composables/types/event'
import type { Locality } from '~/composables/types/locality'
import {
  eventListPageCategoryUrl,
  eventListPageLocalitiesUrl,
  eventListPageLocalityCategoriesUrl,
  eventListPageTagUrl,
} from '~/components/transactionalPage/requestParams'
import type { AlgoliaEventItem } from '~/composables/types/algolia'

export const useTransactionalProductSchema = async ({
  citySlug,
  tagSlug,
  categorySlug,
  searchHeading,
  pageType,
  eventCount = ref(0),
  hits = ref([]),
}: {
  citySlug: ComputedRef<string> | Ref<string | undefined>
  tagSlug: ComputedRef<string> | Ref<string | undefined>
  searchHeading: ComputedRef<string> | Ref<string | null>
  categorySlug: ComputedRef<string | undefined> | Ref<string | undefined>
  eventListPageData?:
    | ComputedRef<StrapiEventListPage>
    | Ref<StrapiEventListPage>
    | StrapiEventListPage
  pageType: EventListPageType
  eventCount: ComputedRef<number>
  hits: ComputedRef<Hit<AlgoliaEventItem>[]>
}) => {
  const nuxtApp = useNuxtApp()
  type TransformedLocalityCategory = {
    permalink: string
    title: string
    slug: string
  }
  const cityPromise = useKftApiFetch<KftApiDefaultResponse<Locality>>(
    eventListPageLocalitiesUrl({ citySlug: citySlug.value }),
    {
      immediate:
        EventListPageType.CITY === pageType ||
        pageType === EventListPageType.CITY_CATEGORY,
    }
  )

  const tagsPromise = useKftApiFetch<KftApiDefaultResponse<Locality>>(
    eventListPageTagUrl({ tagSlug: tagSlug.value }),
    {
      immediate:
        EventListPageType.GENERAL === pageType ||
        EventListPageType.TEAMEVENT === pageType,
    }
  )

  const localityCategoriesPromise = nuxtApp.runWithContext(() =>
    useKftApiFetch<
      KftApiDefaultResponse<LocalityCategory[]>,
      TransformedLocalityCategory[]
    >(eventListPageLocalityCategoriesUrl(), {
      immediate: EventListPageType.CITY_CATEGORY === pageType,
      transform: (res) => {
        // Transform is useful for eliminating large amounts of unnecessary data
        // that will occupy space inside the document (ssr)
        return (
          res?.data?.map((category) => ({
            title: category.title,
            permalink: category.permalink,
            slug: category.slug,
            avg_review: category.avg_review,
            total_reviews: category.total_reviews,
          })) || ([] as TransformedLocalityCategory[])
        )
      },
    })
  )

  const categoryPromise = nuxtApp.runWithContext(() =>
    useKftApiFetch<KftApiDefaultResponse<Category>>(
      eventListPageCategoryUrl({ categorySlug: categorySlug.value }),
      { immediate: EventListPageType.CATEGORY === pageType }
    )
  )

  const [
    { data: cityResponse },
    { data: localityCategoriesResponse },
    { data: categoryResponse },
    { data: tagResponse },
  ] = await Promise.all([
    cityPromise,
    localityCategoriesPromise,
    categoryPromise,
    tagsPromise,
  ])

  const localityCategoryObj = computed(() => {
    const cityCategorySlug = `${citySlug.value}-${categorySlug.value}`

    return (
      localityCategoriesResponse.value?.find(
        (x) => x.slug === cityCategorySlug
      ) || null
    )
  })

  const categoryObj = computed(() => {
    return categoryResponse.value?.data
  })

  const tagObj = computed(() => {
    return tagResponse.value?.data
  })

  const computedReviews = computed(() => {
    const map = {
      // TODO check if these values are right and complete the map
      [EventListPageType.CATEGORY]: categoryObj.value,
      [EventListPageType.CITY]: cityResponse.value?.data,
      [EventListPageType.CITY_CATEGORY]: localityCategoryObj.value,
      [EventListPageType.GENERAL]: tagObj.value,
      [EventListPageType.TEAMEVENT]: tagObj.value,
    } as Record<
      string,
      Category | LocalityCategory | Locality | undefined | null
    >

    return {
      averageRating: map[pageType]?.avg_review,
      totalReviews: map[pageType]?.total_reviews,
    }
  })

  const shouldAppendTransactionalProductSchema = computed(() => {
    return Boolean(computedReviews.value?.totalReviews)
  })

  dayjs.extend(utc)
  dayjs.extend(timezone)

  const minPrice = computed(() => {
    let minPrice = algoliaEventGetters.fnGetMinimumPriceAmount(hits.value?.[0])
    hits.value.forEach((hit) => {
      minPrice = Math.min(
        minPrice,
        Number(algoliaEventGetters.fnGetMinimumPriceAmount(hit)) / 100
      )
    })
    return minPrice
  })
  const maxPrice = computed(() => {
    let maxPrice = 0
    hits.value.forEach((hit) => {
      maxPrice = Math.max(
        maxPrice,
        Number(algoliaEventGetters.fnGetMaximumPriceAmount(hit)) / 100
      )
    })

    return maxPrice
  })
  return {
    shouldAppendTransactionalProductSchema,
    schemaObj: computed(() => {
      return {
        type: 'application/ld+json',
        innerHTML: {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: searchHeading.value,
          brand: { '@type': 'Organization', name: 'konfetti' },
          ...(hits.value.length > 0
            ? {
                offers: {
                  '@type': 'AggregateOffer',
                  lowPrice: minPrice.value,
                  highPrice: maxPrice.value,
                  priceCurrency: algoliaEventGetters.fnGetMinimumPriceCurrency(
                    hits.value[0]
                  ),
                  offerCount: eventCount.value,
                },
              }
            : {}),
          aggregateRating: {
            type: 'AggregateRating',
            ratingValue: computedReviews.value.averageRating,
            reviewCount: computedReviews.value.totalReviews,
            bestRating: 5,
            worstRating: 1,
          },
        },
      }
    }),
  }
}
